import React, { useEffect, useState } from "react";

import { StylesConfig, InputActionMeta } from "react-select";
import CreatableSelect from "react-select/creatable";
import AsyncSelect from "react-select/async";
import projectConfig from "../../../projectConfig.json";

import "./Select.less";

interface Option {
   value: string;
   label: string | React.ReactNode;
   color?: string;
   isFixed?: boolean;
   isDisabled?: boolean;
}

interface SelectProps {
   autoFocus?: boolean;
   className?: string;
   classNamePrefix?: string;
   isDisabled?: boolean;
   isSearchable?: boolean;
   isMulti?: boolean;
   name: string;
   onChange: Function;
   options?: Array<Option>;
   showOptionsMenu?: boolean;
   placeholder: string;
   noOptionsMessage: string;
   closeMenuOnSelect: boolean;
   defaultValue?: object;
   size: "small" | "medium" | "large";
   style?: React.CSSProperties;
   hideArrow: boolean;
   value?: null | Array<Option> | Option;
   isAsync?: boolean;
   isCreatable?: boolean;
   isClearable?: boolean;
   onMenuOpen?: () => void;
   onMenuClose?: () => void;
   components?: Record<string, React.ComponentType<any>>;
   inputValue?: string;
   onInputChange?: (value: string) => void;
}

const passAllOptions = () => true;

export const MultiSelect: React.FC<SelectProps> = ({
   autoFocus = false,
   isMulti = true,
   closeMenuOnSelect = false,
   className,
   classNamePrefix,
   isDisabled,
   isSearchable,
   name,
   onChange,
   options,
   showOptionsMenu,
   noOptionsMessage,
   placeholder = "",
   size = "medium",
   style,
   defaultValue,
   hideArrow = false,
   value,
   isAsync = false,
   isCreatable = true,
   isClearable = false,
   onMenuOpen,
   onMenuClose,
   components,
   inputValue,
   onInputChange,
}) => {
   const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

   const chooseSize = (size: string) => {
      if (size === "small") {
         return "200px";
      } else if (size === "medium") {
         return "400px";
      } else if (size === "large") {
         return "600px";
      } else return size;
   };

   const defaultStyles = {
      "&:active": { borderColor: projectConfig?.cssVariables?.["--highlight"] },
      "&:hover": {
         borderColor: projectConfig?.cssVariables?.["--second-hover"],
         borderWidth: "1px",
      },
      borderRadius: "6px",
   };

   const customStyles: StylesConfig = {
      control: (baseStyles: any, state: any) => ({
         ...baseStyles,
         ...defaultStyles,
         width: chooseSize(size),
         "&:hover": { borderColor: projectConfig?.cssVariables?.["--btn-primaryBackground"] },
         borderColor: state.isFocused
            ? projectConfig?.cssVariables?.["--btn-primaryBackground"]
            : projectConfig?.cssVariables?.["--btn-defaultBorder"],
         boxShadow: "none",
         ...style,
      }),
      menuList: (provided) => ({
         ...provided,
         overflowX: "hidden",
      }),
      option: (provided, state) => ({
         ...provided,
         minHeight: "32px",
         backgroundColor: state.isFocused
            ? projectConfig.cssVariables["--btn-primaryBackground"]
            : state.isSelected
              ? projectConfig.cssVariables["--btn-primaryBackground"]
              : "transparent",
         color:
            state.isSelected || state.isFocused
               ? projectConfig.cssVariables["--nav-background"]
               : "inherit",
         ":active": {
            ...provided[":active"],
            backgroundColor: projectConfig.cssVariables["--btn-primaryBackground"],
         },
      }),
      menu: (base) => ({
         ...base,
         position: "absolute",
         marginTop: 4,
      }),
      menuPortal: (base) => ({
         ...base,
         zIndex: 9999,
         position: "fixed",
      }),
      singleValue: (base) => ({
         ...base,
         transform: "none",
         position: "relative",
      }),
      valueContainer: (base) => ({
         ...base,
         flexWrap: "nowrap",
      }),
   };

   // Async
   const filterOptions = (inputValue: string) => {
      return options?.filter((i) =>
         i?.label?.toString().toLowerCase()?.includes(inputValue.toLowerCase()),
      );
   };

   const promiseOptions = (inputValue: string) =>
      new Promise<any[]>((resolve) => {
         setTimeout(() => {
            //@ts-ignore
            resolve(filterOptions(inputValue));
         }, 1000);
      });

   useEffect(() => {
      const portalElement = document.getElementById("dropdown-root");
      setPortalTarget(portalElement);
   }, []);

   return (
      <>
         {isAsync ? (
            <AsyncSelect
               cacheOptions
               defaultOptions
               loadOptions={promiseOptions}
               name={name}
               isDisabled={isDisabled}
               isValidNewOption={() => isCreatable}
               classNamePrefix={classNamePrefix}
               className={`bp2-multiselect ${className} ${hideArrow ? "arrow-hidden" : ""}`}
               placeholder={placeholder}
               isMulti={isMulti}
               onChange={onChange}
               isClearable={isClearable}
               onMenuOpen={onMenuOpen}
               onMenuClose={onMenuClose}
               menuPortalTarget={portalTarget || undefined}
               styles={customStyles}
               components={components}
               // defaultValue={defaultValue}
               // closeMenuOnSelect={closeMenuOnSelect}
               // noOptionsMessage={() => noOptionsMessage}
               // menuIsOpen={showOptionsMenu}
            />
         ) : (
            <CreatableSelect
               defaultValue={defaultValue}
               placeholder={placeholder}
               closeMenuOnSelect={closeMenuOnSelect}
               noOptionsMessage={() => noOptionsMessage}
               name={name}
               isSearchable={isSearchable}
               isDisabled={isDisabled}
               isValidNewOption={() => isCreatable}
               classNamePrefix={classNamePrefix}
               className={`bp2-multiselect ${className} ${hideArrow ? "arrow-hidden" : ""}`}
               autoFocus={autoFocus}
               isMulti={isMulti}
               menuIsOpen={showOptionsMenu}
               styles={customStyles}
               onChange={onChange}
               options={options}
               value={value}
               isClearable={isClearable}
               onMenuOpen={onMenuOpen}
               onMenuClose={onMenuClose}
               menuPortalTarget={portalTarget || undefined}
               components={components}
               {...(onInputChange !== undefined && {
                  inputValue,
                  onInputChange: (value: string, { action }: InputActionMeta) => {
                     if (action === "input-change") onInputChange(value);
                  },
                  filterOption: passAllOptions,
               })}
            />
         )}
      </>
   );
};
